import {
  ClearButton,
  Button,
  SearchInput,
  Menu,
  MenuList,
  ButtonText,
  Badge,
  MenuItem,
  MenuDivider,
  Modal
} from '@morressier/alembic';
import { IMyReviewListItem } from '@publishing-engine/domain/views';
import {
  Flex,
  Text,
  Tooltip,
  Box,
  Th,
  Tr,
  Td,
  Table,
  Tbody,
  Thead,
  MenuButton,
  Skeleton,
  Divider,
  Toast
} from '@chakra-ui/react';
import {
  useReviewDashBoardConfig,
  useReviews,
  defaultValues,
  useReviewerSavedFilters
} from './hooks';
import {
  BaseBadge,
  recommendationBadges,
  ContextualReviewBadge
} from 'src/components/Review/Badges';
import NoReviewsFound from './EmptyState';
import RichTextPreview from 'src/components/Submission/GenericFieldComponents/RichText/RichTextPreview';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBarsFilter,
  faDeleteRight,
  faFloppyDisk,
  faSearch,
  faXmark
} from '@fortawesome/pro-regular-svg-icons';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IMyReviewsFilters } from 'src/api/queries';
import SortMenu, {
  SortField
} from '../Organization/public/Journal/SubmissionUpload/components/common/Sort/SortMenu';
import {
  parseReviewQueryParams,
  updateQueryParams
} from 'src/utils/submissionsList';
import FilterDrawer from '../Organization/public/Journal/SubmissionUpload/components/common/Filters/FilterDrawer';
import ListLoader from 'src/components/ListLoader';
import { Helmet } from 'react-helmet';
import { ISavedFilterSets, IWorkPhase } from '@publishing-engine/domain/models';
import { SUBMISSION_STATUS_MAP } from 'src/constants';
import { getPhaseContent } from 'src/utils/phaseCopy';
import SaveFiltersModal from '../Organization/public/Journal/SubmissionUpload/components/common/Filters/SaveFilterModal';
import ManageSavedFiltersModal from '../Organization/public/Journal/SubmissionUpload/components/common/Filters/ManageSavedFiltersModal';
import SaveFilterText from '../Organization/Workspace/Submissions/SaveFilterText';
import SubmissionActionMenu from '../Organization/Workspace/Submissions/SubmissionActionMenu';
import ContactJournal from 'src/components/ContactPublisher/ContactPublisher';
import TablePagination, {
  DEFAULT_PAGE_SIZE
} from 'src/components/TablePagination';

interface TableColumn {
  key: string;
  label: string;
  size: number;
}

const COMPLETED_COLUMNS: TableColumn[] = [
  { key: 'title', label: 'Title', size: 2 },
  { key: 'journal', label: 'Journal', size: 2 },
  { key: 'reviewStatus', label: 'Review Status', size: 1 },
  { key: 'recommendation', label: 'Recommendation', size: 2 },
  { key: 'submissionStatus', label: 'Submission Status', size: 2 }
];

const ReviewTableHeader = () => (
  <Thead>
    <Tr
      data-testId="submissions-table-header"
      gap={4}
      sx={{
        '& th': {
          textTransform: 'none',
          color: 'black',
          background: 'white'
        }
      }}
    >
      {COMPLETED_COLUMNS.map(column => (
        <Th p={2} key={column.key} flex={column.size}>
          <Text textStyle="headingItem" whiteSpace="nowrap">
            {column.label}
          </Text>
        </Th>
      ))}
      <Th></Th>
    </Tr>
  </Thead>
);

const ReviewRow = ({ review }: { review: IMyReviewListItem }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const {
    submission: { title, manuscriptId },
    submissionContext,
    submissionCurrentWorkflowState,
    caseData: { latestRecommendation },
    workflow: { currentState },
    id
  } = review;

  const isClickable = currentState !== 'declined';

  const submissionPhaseState = submissionCurrentWorkflowState || 'initial';
  const submissionStatus = SUBMISSION_STATUS_MAP[submissionPhaseState];
  const submissionPhaseHeading = getPhaseContent(
    submissionPhaseState as IWorkPhase,
    'reviewer'
  ).heading as string;

  return (
    <>
      <Tr
        key={id}
        data-testId="review-completed-table-row"
        gap={4}
        background="white"
      >
        <Td
          flex={COMPLETED_COLUMNS[0]?.size}
          textStyle="headingLine"
          paddingX={2}
          onClick={() => {
            if (isClickable) {
              navigate(`/app/reviews/${id}?${searchParams.toString()}`, {
                state: { from: location.pathname + location.search }
              });
            }
          }}
          cursor={isClickable ? 'pointer' : 'default'}
          _hover={{ bg: 'gray.50' }}
        >
          <Box width="100%">
            {isClickable ? (
              <ClearButton
                ariaLabel={`open ${title} review`}
                display="block"
                width="100%"
                textAlign="left"
                onClick={() =>
                  navigate(`/app/reviews/${id}?${searchParams.toString()}`, {
                    state: { from: location.pathname + location.search }
                  })
                }
              >
                <Tooltip
                  hasArrow
                  borderRadius={4}
                  placement="top"
                  label={'View Review'}
                >
                  <Box width="100%">
                    <Text textStyle="headingLine" whiteSpace="normal">
                      {manuscriptId}
                    </Text>
                  </Box>
                  <Box isTruncated noOfLines={1} width="100%">
                    <Text
                      textStyle="textBody"
                      whiteSpace="normal"
                      textDecoration="none"
                      _hover={{
                        textDecoration: 'underline',
                        textDecorationColor: 'actionPrimaryFill'
                      }}
                    >
                      <RichTextPreview
                        value={title}
                        color="actionPrimaryFill"
                      />
                    </Text>
                  </Box>
                </Tooltip>
              </ClearButton>
            ) : (
              <>
                <Box width="100%">
                  <Text textStyle="headingLine" whiteSpace="normal">
                    {manuscriptId}
                  </Text>
                </Box>

                <Box isTruncated noOfLines={1} width="100%">
                  <Text textStyle="textBody" whiteSpace="normal">
                    <RichTextPreview value={title} />
                  </Text>
                </Box>
              </>
            )}
          </Box>
        </Td>
        <Td
          flex={COMPLETED_COLUMNS[2]?.size}
          textTransform="uppercase"
          paddingX={2}
        >
          {submissionContext?.data?.shortname}
        </Td>
        <Td flex={COMPLETED_COLUMNS[4]?.size} paddingX={2}>
          <ContextualReviewBadge
            reviewState={review.workflow.currentState}
            context="reviewer"
          />
        </Td>
        <Td flex={COMPLETED_COLUMNS[3]?.size} paddingX={2}>
          {latestRecommendation
            ? recommendationBadges[latestRecommendation]
            : recommendationBadges.default}
        </Td>
        <Td flex={COMPLETED_COLUMNS[4]?.size} paddingX={2}>
          {submissionStatus ? (
            <BaseBadge
              icon={submissionStatus.icon}
              variant={submissionStatus.variant}
              label={submissionPhaseHeading || submissionStatus.label}
            />
          ) : null}
        </Td>
        <Td textStyle="textBody">
          <Flex justifyContent={'flex-end'}>
            <SubmissionActionMenu
              submissionId={id}
              handleClick={() => {
                navigate(`/app/reviews/${id}?${searchParams.toString()}`, {
                  state: { from: location.pathname + location.search }
                });
              }}
              handleContactClick={() => {
                setIsOpen(true);
              }}
              isReview={true}
              isViewDisabled={!isClickable}
            />
          </Flex>
        </Td>
      </Tr>
      {review && (
        <Modal width="720px" isOpen={isOpen} onClose={() => setIsOpen(false)}>
          <ContactJournal
            submissionId={review?.submission?.id}
            submissionContextId={review?.submissionContext?.id!}
            manuscriptId={review?.submission?.manuscriptId!}
            setIsContactModalOpen={setIsOpen}
            reviewRequestId={id}
          />
        </Modal>
      )}
    </>
  );
};

const CompletedReviews = () => {
  const { watch, reset, setValue, getValues } = useForm<IMyReviewsFilters>({
    defaultValues: {
      ...defaultValues,
      reviewListType: 'completed'
    }
  });

  const { data: dashBoardConfigs } =
    useReviewDashBoardConfig('reviews-completed');

  const [searchValue, setSearchValue] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >({});
  const [sort, setSort] = useState<SortField | null>(null);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [filterName, setFilterName] = useState<string | null>(null);
  const [showSaveFiltersModal, setShowSaveFiltersModal] =
    useState<boolean>(false);
  const [showManageFiltersModal, setShowManageFiltersModal] =
    useState<boolean>(false);
  const filters = watch();
  const navigate = useNavigate();
  const location = useLocation();

  const offset = (page - 1) * pageSize;
  const { data, isLoading } = useReviews(
    'completed',
    page,
    offset,
    filters,
    sort ?? undefined,
    pageSize
  );

  const { data: savedFilters, isLoading: isSavedFiltersLoading } =
    useReviewerSavedFilters('reviews-completed');

  const clearFilters = () => {
    reset({ ...defaultValues, reviewListType: 'completed' });
    setSearchValue('');
    setSelectedFilters({});
    navigate({
      pathname: location.pathname,
      search: '?reviewListType=completed'
    });
  };

  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        journalId: filters.journalId ?? [],
        searchParams: (filters.searchParams ?? '').trim()
      }),
    [filters.journalId, filters.searchParams]
  );
  useEffect(() => {
    setPage(1);
  }, [filtersKey]);

  // Parse query parameters on initial load
  useEffect(() => {
    const { filters: newFilters, sort: newSort } = parseReviewQueryParams(
      location.search
    );
    if (newFilters.reviewListType === 'completed') {
      setSearchValue(newFilters.searchParams);
      reset(newFilters);
      setSort(newSort);
    }
  }, [reset]);

  // Sync URL query params to formState
  useEffect(() => {
    updateQueryParams(
      {
        ...filters,
        sort: sort ? `${sort.label},${sort.value},${sort.direction}` : ''
      },
      location,
      navigate
    );
  }, [filters, sort, navigate]);
  const total = data?.pages?.[data.pages.length - 1]?.total ?? 0;
  const hasFilters = !!filters.journalId.length || !!filters.searchParams;

  const handleFiltersSubmit = (selectedFilters: Record<string, string[]>) => {
    Object.entries(selectedFilters).forEach(([key, values]) => {
      setValue(key as keyof IMyReviewsFilters, values as any);
    });
  };

  const startIndex = offset + 1;
  const endIndex = Math.min(offset + pageSize, total);

  const handleSortApply = (selected: SortField | null) => {
    setSort(selected);
    setPage(1);
  };

  const handleSortReset = () => {
    setSort(null);
    setPage(1);
  };

  const removeSingleFilter = (
    filterToRemove: string,
    fieldName: keyof IMyReviewsFilters
  ) => {
    const currentFilter = getValues(fieldName) as string[];
    const updatedFilter = currentFilter.filter(
      filter => filter !== filterToRemove
    );
    setValue(fieldName, updatedFilter, {
      shouldValidate: true,
      shouldDirty: true
    });
    setSelectedFilters(prev => ({
      ...prev,
      [fieldName]: updatedFilter
    }));
    setFilterName(null);
  };

  const handleSetValues = (data: ISavedFilterSets, name: string) => {
    setValue('searchParams', data?.searchParams ?? '');
    setValue('journalId', data?.journalId ?? []);
    setFilterName(name);
    setSearchValue(data?.searchParams ?? '');
    setSearchValue(data?.searchParams ?? '');
    setSelectedFilters({
      journalId: data?.journalId ?? []
    });
    return Toast({
      variant: 'success',
      title: `${name} Filter Applied`
    });
  };

  const onDeleteClearFilters = () => {
    clearFilters();
    setFilterName(null);
  };

  const handleSaveFiltersModal = (name: string | null) => {
    setShowSaveFiltersModal(prev => !prev);
    if (name) setFilterName(name);
  };

  return (
    <>
      <Flex flexDirection={'column'} gap={4} width="100%">
        <Helmet>
          <title>Completed Reviews</title>
        </Helmet>
        {showSaveFiltersModal ? (
          <SaveFiltersModal
            isOpen={showSaveFiltersModal}
            onClose={handleSaveFiltersModal}
            filter={filters}
            view="reviews-completed"
          />
        ) : null}
        {showManageFiltersModal ? (
          <ManageSavedFiltersModal
            isOpen={showManageFiltersModal}
            onClose={() => setShowManageFiltersModal(false)}
            savedFilters={savedFilters?.response}
            availableFilters={dashBoardConfigs?.filters}
            isSavedFiltersLoading={isSavedFiltersLoading}
            clearFilters={onDeleteClearFilters}
            view="reviews-completed"
          />
        ) : null}
        <Flex gap={2} flexDirection={'column'}>
          <Flex
            gap={3}
            mb={2}
            width="100%"
            alignItems={{ base: 'stretch', md: 'center' }}
            flexDirection={{ base: 'column', md: 'row' }}
          >
            <Box width="100%">
              <Flex gap={2} alignItems="center">
                <SearchInput
                  placeholder="Search submissions by title or manuscript ID"
                  iconLeft={<FontAwesomeIcon icon={faSearch} />}
                  width="100%"
                  name="searchParams"
                  value={searchValue}
                  enterKeyHint="search"
                  onChange={e => setSearchValue(e.currentTarget.value)}
                  onInputClear={() => {
                    setSearchValue('');
                    setValue('searchParams', '');
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setValue('searchParams', searchValue.trim());
                      setFilterName(null);
                    }
                  }}
                  onKeyUp={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setValue('searchParams', searchValue.trim());
                      setFilterName(null);
                    }
                  }}
                />
                <Box display={{ base: 'block', lg: 'none' }} flexShrink={0}>
                  <Button
                    variant="primary"
                    aria-label="Search"
                    onClick={() => {
                      setValue('searchParams', searchValue.trim());
                      setFilterName(null);
                    }}
                  >
                    <FontAwesomeIcon icon={faSearch} />
                  </Button>
                </Box>
              </Flex>
            </Box>
            {dashBoardConfigs?.filters && (
              <Box width={{ base: '100%', md: 'auto' }}>
                <FilterDrawer
                  filterGroups={dashBoardConfigs.filters.map((filter: any) => ({
                    ...filter,
                    items: filter.items ?? []
                  }))}
                  watch={watch}
                  onSubmit={handleFiltersSubmit}
                  selectedFilters={selectedFilters}
                  setSelectedFilters={setSelectedFilters}
                />
              </Box>
            )}
            <Flex width={{ base: '100%', md: 'auto' }}>
              <Menu ariaLabel="filters">
                {({ onClose }) => (
                  <>
                    <MenuButton
                      as={Button}
                      variant="secondary"
                      iconLeft={<FontAwesomeIcon icon={faBarsFilter} />}
                      width={{ base: '100%', md: 'auto' }}
                    >
                      Saved Filters
                    </MenuButton>
                    <MenuList
                      textStyle="headingItem"
                      minWidth={{ base: 'calc(100vw - 2rem)', md: 'auto' }}
                      maxWidth={{ base: 'calc(100vw - 2rem)', md: '420px' }}
                    >
                      {savedFilters?.response?.length ? (
                        <>
                          <Box maxH="300px" overflowY="auto" maxW="350px">
                            {savedFilters.response.map(item => (
                              <>
                                <MenuItem
                                  onClick={() => {
                                    const filter =
                                      item.filters as ISavedFilterSets;
                                    handleSetValues(filter, item.name);
                                  }}
                                  ariaLabel={item.name}
                                  key={item.id}
                                >
                                  <Text textStyle="headingLine" isTruncated>
                                    {item.name}
                                  </Text>
                                  {
                                    <SaveFilterText
                                      saveFilters={
                                        item.filters as ISavedFilterSets
                                      }
                                      availableFilters={
                                        dashBoardConfigs?.filters
                                      }
                                      manageTags={[]}
                                    />
                                  }
                                </MenuItem>
                                <MenuDivider />
                              </>
                            ))}
                          </Box>
                          <Flex
                            alignItems="center"
                            justifyContent="center"
                            padding={3}
                            position="sticky"
                          >
                            <ButtonText
                              onClick={() => {
                                setShowManageFiltersModal(true);
                                onClose();
                              }}
                            >
                              Manage Filters
                            </ButtonText>
                          </Flex>
                        </>
                      ) : (
                        <Flex
                          flexDirection={'column'}
                          gap={4}
                          padding={{ base: 4, md: 8 }}
                          justifyContent={'center'}
                          alignItems={'center'}
                          maxW="350px"
                        >
                          <Text textStyle="headingGroup">No Saved Filters</Text>
                          <Text textStyle="textBody" textAlign={'center'}>
                            You do not have any saved filters yet. Try applying
                            some filters and then click 'Save Filters' to save
                            them for later usage.
                          </Text>
                        </Flex>
                      )}
                    </MenuList>
                  </>
                )}
              </Menu>
            </Flex>
          </Flex>
        </Flex>
        <Skeleton isLoaded={!isLoading}>
          <Flex
            alignItems={'center'}
            gap={4}
            height={5}
            justifyContent={'space-between'}
          >
            <Flex>
              <Text textStyle="textBody">
                {total > 0 ? (
                  <>
                    Showing <strong>{startIndex}</strong> –{' '}
                    <strong>{endIndex}</strong> of <strong>{total}</strong>{' '}
                    completed review{total === 1 ? '' : 's'}
                  </>
                ) : (
                  <>
                    <strong>0</strong> completed review
                  </>
                )}
              </Text>
              {hasFilters && (
                <>
                  {filterName ? (
                    <Box marginLeft={2}>
                      <Text textStyle="textBody">
                        <strong>{filterName}</strong> applied
                      </Text>
                    </Box>
                  ) : (
                    <ButtonText
                      leftIcon={<FontAwesomeIcon icon={faFloppyDisk} />}
                      variant="secondary"
                      onClick={() => setShowSaveFiltersModal(true)}
                      marginX={2}
                    >
                      Save Filters
                    </ButtonText>
                  )}
                  <ButtonText
                    leftIcon={<FontAwesomeIcon icon={faDeleteRight} />}
                    variant="secondary"
                    onClick={() => {
                      clearFilters();
                      setFilterName(null);
                    }}
                  >
                    Clear Filters
                  </ButtonText>
                </>
              )}
            </Flex>
            <Divider
              orientation="horizontal"
              flex="1"
              mx={4}
              borderColor="gray.300"
            />
            {dashBoardConfigs?.sortOptions?.fields && (
              <SortMenu
                appliedSort={sort}
                options={dashBoardConfigs.sortOptions}
                onApply={handleSortApply}
                onReset={handleSortReset}
              />
            )}
          </Flex>
          {!!hasFilters && (
            <Flex alignItems="center" gap={2} wrap="wrap" marginTop={2}>
              {dashBoardConfigs?.filters?.map(filterConfig => {
                // Type guard to check if filterConfig has 'items'
                const hasItems = (fc: any): fc is { items: any[] } =>
                  Array.isArray(fc.items);

                if (
                  !hasItems(filterConfig) ||
                  filterConfig.items.length === 0
                ) {
                  return null;
                }

                const filterKey = filterConfig.key as keyof typeof filters;
                const filterValues = filters?.[filterKey];

                if (!Array.isArray(filterValues) || filterValues.length === 0) {
                  return null;
                }

                return filterValues.map(value => {
                  const item = filterConfig.items.find(
                    (i: any) => i.value === value
                  );
                  const label = item?.name ?? value;

                  return (
                    <Badge
                      key={`${filterKey}-${value}`}
                      size="base"
                      variant="interactive"
                    >
                      <Flex alignItems="center" gap={2}>
                        {label}
                        <FontAwesomeIcon
                          icon={faXmark}
                          cursor="pointer"
                          onClick={() =>
                            removeSingleFilter(
                              value,
                              filterKey as keyof IMyReviewsFilters
                            )
                          }
                        />
                      </Flex>
                    </Badge>
                  );
                });
              })}
            </Flex>
          )}
        </Skeleton>
        <Box width="100%">
          {isLoading && <ListLoader />}
          {data?.pages[0] && total !== 0 ? (
            <>
              <Box mx={-16}>
                <Box px={16}>
                  <Flex flexDirection={'column'}>
                    {/* @ts-ignore */}
                    <Table size={'sm'} width="1010px" minWidth="100%">
                      <ReviewTableHeader />
                      <Tbody>
                        {data?.pages
                          .flatMap(page => page.items)
                          .map(review => (
                            <ReviewRow key={review.id} review={review} />
                          ))}
                      </Tbody>
                    </Table>
                  </Flex>
                </Box>
              </Box>
              <TablePagination
                ariaLabel="Submission pagination"
                currentPage={page}
                totalItems={total}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </>
          ) : (
            !isLoading && <NoReviewsFound />
          )}
        </Box>
      </Flex>
    </>
  );
};
export default CompletedReviews;
